// src/features/tenant/TenantCreateDialog.tsx
// Create a tenant — form-kit dialog (docs/ux/14-forms.md), opened from
// TenantListPage's "+ New" instead of routing to a /tenants/new page.
// Follows the TeamCreateDialog precedent exactly.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useTenantTenant } from '@react/hooks';
import { FormDialog, TitleInput, AutoGrowTextarea, SegmentedControl, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import type { Tenant } from '@core/types/tenant';

const TYPE_OPTIONS = [
  { value: 'TENANT_TYPE_PERSONAL', labelKey: 'tenant.typePersonal' },
  { value: 'TENANT_TYPE_TEAM', labelKey: 'tenant.typeTeam' },
  { value: 'TENANT_TYPE_ORGANIZATION', labelKey: 'tenant.typeOrganization' },
  { value: 'TENANT_TYPE_ENTERPRISE', labelKey: 'tenant.typeEnterprise' },
];

const REGION_OPTIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
];

const ISOLATION_OPTIONS = [
  { value: 'TENANT_ISOLATION_LEVEL_SHARED', labelKey: 'tenant.isolationShared' },
  { value: 'TENANT_ISOLATION_LEVEL_DEDICATED_DB', labelKey: 'tenant.isolationDedicatedDb' },
  { value: 'TENANT_ISOLATION_LEVEL_DEDICATED', labelKey: 'tenant.isolationDedicated' },
];

/** Slugify a name into a URL-safe slug preview. */
function slugify(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export interface TenantCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string | undefined) => void;
}

export function TenantCreateDialog({ open, onClose, onCreated }: TenantCreateDialogProps) {
  const { t } = useI18n();
  const store = useTenantTenant();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('TENANT_TYPE_TEAM');
  const [region, setRegion] = useState('us-east-1');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [isolationLevel, setIsolationLevel] = useState('TENANT_ISOLATION_LEVEL_SHARED');
  const [moreFields, setMoreFields] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setType('TENANT_TYPE_TEAM');
    setRegion('us-east-1');
    setSlug('');
    setSlugTouched(false);
    setIsolationLevel('TENANT_ISOLATION_LEVEL_SHARED');
    setMoreFields(false);
    setError(null);
  }, [open]);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const dirty = !!(name.trim() || description.trim());

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const tenant: Tenant = {
        name: name.trim(),
        slug: effectiveSlug || slugify(name.trim()),
        description: description.trim(),
        type: type as Tenant['type'],
        region,
        isolation_level: isolationLevel as Tenant['isolation_level'],
      } as Tenant;
      const created = await store.CreateTenant({ tenant });
      const newId = created?.entity?.id;
      onCreated(newId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('tenant.create')}
      canSubmit={!!name.trim()}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('tenant.create')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('common.fewerFields') : t('common.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <TitleInput
          value={name}
          onChange={setName}
          placeholder={t('tenant.namePlaceholder')}
          initialFocus
          aria-label={t('tenant.name')}
        />
        <AutoGrowTextarea
          value={description}
          onChange={setDescription}
          placeholder={t('tenant.descriptionPlaceholder')}
          minRows={1}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 px-3.5 pb-3">
        <SegmentedControl
          aria-label={t('tenant.type')}
          size="sm"
          value={type}
          onChange={setType}
          options={TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
        />
        <div className="w-48">
          <Select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            aria-label={t('tenant.region')}
            options={REGION_OPTIONS}
          />
        </div>
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('tenant.slug')} labelWidth="sm" hint={t('tenant.slugHint')}>
            <input
              type="text"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              }}
              placeholder="my-organization"
              className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('tenant.isolationLevel')} labelWidth="sm">
            <SegmentedControl
              aria-label={t('tenant.isolationLevel')}
              size="sm"
              value={isolationLevel}
              onChange={setIsolationLevel}
              options={ISOLATION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          </FormRow>
        </div>
      )}
    </FormDialog>
  );
}

export default TenantCreateDialog;
